import { useContext, useEffect, useState, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { MapContainer, TileLayer, Marker, Popup, Tooltip, useMap } from "react-leaflet";
import L from "leaflet";
import moment from "moment";
import { Card, Row, Col, Button, Spin, Typography, DatePicker, Table, Modal, Image, Empty } from "antd";
import {
  ArrowLeftOutlined,
  CheckCircleOutlined,
  EyeOutlined,
  ClockCircleOutlined,
} from "@ant-design/icons";
import "leaflet/dist/leaflet.css";
import { AppStateContext } from "../../context/AppContext";
import useRequest from "../../hooks/useRequest";
import apiPath from "../../constants/apiPath";

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png",
  iconUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png",
  shadowUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png",
});

const defaultCenter = [28.6139, 77.209];
const MAP_HEIGHT = 520;
const MAP_ZOOM = 14;          
const EARTH_RADIUS_KM = 6371;

function hasValidCoordinates(location) {
  const latitude = Number(location?.latitude);
  const longitude = Number(location?.longitude);

  return (
    Number.isFinite(latitude) &&
    Number.isFinite(longitude) &&
    latitude >= -90 &&
    latitude <= 90 &&
    longitude >= -180 &&
    longitude <= 180
  );
}

function distanceBetweenLocationsInKm(first, second) {
  const toRadians = (value) => (value * Math.PI) / 180;
  const firstLatitude = toRadians(Number(first.latitude));
  const secondLatitude = toRadians(Number(second.latitude));
  const latitudeDifference = secondLatitude - firstLatitude;
  const longitudeDifference = toRadians(Number(second.longitude) - Number(first.longitude));
  const haversine =
    Math.sin(latitudeDifference / 2) ** 2 +
    Math.cos(firstLatitude) *
      Math.cos(secondLatitude) *
      Math.sin(longitudeDifference / 2) ** 2;

  return 2 * EARTH_RADIUS_KM * Math.asin(Math.min(1, Math.sqrt(haversine)));
}

function formatDistance(kilometers) {
  if (kilometers < 1) return `${Math.round(kilometers * 1000)} m`;
  return `${kilometers.toFixed(2)} km`;
}

function MapFitBounds({ locations }) {
  const map = useMap();
  useEffect(() => {
    if (!locations || locations.length === 0) return;
    const points = locations
      .filter((loc) => loc.latitude != null && loc.longitude != null)
      .map((loc) => [Number(loc.latitude), Number(loc.longitude)]);
    if (points.length === 0) return;
    if (points.length === 1) {
      map.flyTo(points[0], MAP_ZOOM);
    } else {
      const bounds = L.latLngBounds(points);
      map.flyToBounds(bounds, { padding: [40, 40], maxZoom: 14 });
    }
  }, [locations, map]);
  return null;
}

function UserLocationMapPage() {
  const { userId } = useParams();
  const navigate = useNavigate();
  const { setPageHeading } = useContext(AppStateContext);
  const { request } = useRequest();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [selectedDate, setSelectedDate] = useState(() => moment());
  const [previewVisible, setPreviewVisible] = useState(false);
  const [screenTimeVisible, setScreenTimeVisible] = useState(false);
  const [screenTimeLoading, setScreenTimeLoading] = useState(false);
  const [screenTimeSummary, setScreenTimeSummary] = useState(null);

  useEffect(() => {
    setPageHeading("User Location");
  }, [setPageHeading]);

  useEffect(() => {
    if (!userId) {
      setError("Invalid user");
      setLoading(false);
      return;
    }
    request({
      url: `${apiPath.getUserLocation}/${userId}`,
      method: "GET",
      onSuccess: (response) => {
        setData(response && response.data ? response.data : null);
        setError(null);
        setLoading(false);
      },
      onError: (err) => {
        setError(err?.message || "Failed to load location");
        setLoading(false);
      },
    });
  }, [userId]);

  const user = data?.user;
  const allLocations = useMemo(
    () => (Array.isArray(data?.locations) ? data.locations : []),
    [data?.locations]
  );

  const locations = useMemo(() => {
    if (!selectedDate) return allLocations;
    const selectedDay = moment(selectedDate).format("YYYY-MM-DD");
    return allLocations.filter((loc) => {
      if (!loc.mailReceivedAt) return true;
      const locDay = moment.utc(loc.mailReceivedAt).local().format("YYYY-MM-DD");
      return locDay === selectedDay;
    });
  }, [allLocations, selectedDate]);

  const orderedLocations = useMemo(
    () =>
      [...locations].sort((first, second) => {
        const firstTime = first.mailReceivedAt && moment(first.mailReceivedAt).isValid()
          ? moment(first.mailReceivedAt).valueOf()
          : Number.MAX_SAFE_INTEGER;
        const secondTime = second.mailReceivedAt && moment(second.mailReceivedAt).isValid()
          ? moment(second.mailReceivedAt).valueOf()
          : Number.MAX_SAFE_INTEGER;
        return firstTime - secondTime;
      }),
    [locations]
  );
  const routeLocations = useMemo(
    () => orderedLocations.filter(hasValidCoordinates),
    [orderedLocations]
  );
  const hasLocations = routeLocations.length > 0;
  const totalTravelDistance = useMemo(
    () =>
      routeLocations.reduce(
        (total, location, index) =>
          index === 0
            ? total
            : total + distanceBetweenLocationsInKm(routeLocations[index - 1], location),
        0
      ),
    [routeLocations]
  );
  const imageTimeline = useMemo(
    () =>
      locations
        .filter((loc) => loc?.imageUrl)
        .map((loc, index) => ({
          key: `${loc.imageUrl}-${index}`,
          imageUrl: loc.imageUrl,
          timeText: loc.mailReceivedAt ? moment(loc.mailReceivedAt).format("DD MMM YYYY, hh:mm A") : "Time not available",
          locationText: loc.location || "Location not available",
          timestamp: loc.mailReceivedAt && moment(loc.mailReceivedAt).isValid()
            ? moment(loc.mailReceivedAt).valueOf()
            : null,
        }))
        .sort((a, b) => {
          if (a.timestamp == null) return 1;
          if (b.timestamp == null) return -1;
          return a.timestamp - b.timestamp;
        }),
    [locations]
  );
  const totalWorkingHours = useMemo(() => {
    const selfieTimes = imageTimeline
      .map((item) => item.timestamp)
      .filter((timestamp) => timestamp != null);

    if (!selfieTimes.length) return "—";

    const duration = moment.duration(
      selfieTimes[selfieTimes.length - 1] - selfieTimes[0]
    );
    const totalMinutes = Math.floor(duration.asMinutes());
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;

    return hours > 0
      ? `${hours} hr${hours === 1 ? "" : "s"} ${minutes} min`
      : `${minutes} min`;
  }, [imageTimeline]);
  const mapCenter = hasLocations
    ? [Number(routeLocations[0].latitude), Number(routeLocations[0].longitude)]
    : defaultCenter;

  const openScreenTimeSummary = () => {
    setScreenTimeVisible(true);
    setScreenTimeLoading(true);
    request({
      url: `${apiPath.getUserScreenTimeSummary}/${userId}?date=${moment(selectedDate).format("YYYY-MM-DD")}`,
      method: "GET",
      onSuccess: (response) => {
        setScreenTimeSummary(response?.data || null);
        setScreenTimeLoading(false);
      },
      onError: () => {
        setScreenTimeSummary(null);
        setScreenTimeLoading(false);
      },
    });
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: 320 }}>
        <Spin size="large" tip="Loading location…" />
      </div>
    );
  }

  if (error || !user) {
    return (
      <Card>
        <Typography.Text type="danger">{error || "User not found"}</Typography.Text>
        <div style={{ marginTop: 12 }}>
          <Button type="primary" icon={<ArrowLeftOutlined />} onClick={() => navigate("/user-management")}>
            Back to User Management
          </Button>
        </div>
      </Card>
    );
  }

  return (
    <div>
      <Row gutter={[16, 16]} align="middle">
        <Col flex="none">
          <Button
            type="text"
            icon={<ArrowLeftOutlined />}
            onClick={() => navigate("/user-management")}
          >
            Back to User Management
          </Button>
        </Col>
        <Col flex="auto" style={{ minWidth: 0 }}>
          <Card size="small" bodyStyle={{ padding: "8px 16px" }} style={{ display: "inline-block", maxWidth: "100%" }}>
            <span style={{ display: "inline-block", marginRight: 20 }}><strong>Name:</strong> {user.name || "—"}</span>
            <span style={{ display: "inline-block", marginRight: 20 }}><strong>Mobile:</strong> {user.mobileNumber || "—"}</span>
            <span style={{ display: "inline-block", marginRight: 20 }}>
              <strong>Total Working Hours:</strong> {totalWorkingHours}
            </span>
            <span style={{ display: "inline-block", marginRight: 20 }}>
              <strong>Total distance traveled:</strong> {formatDistance(totalTravelDistance)}
            </span>
            <Button
              type="primary"
              size="small"
              icon={<EyeOutlined />}
              disabled={!imageTimeline.length}
              onClick={() => setPreviewVisible(true)}
            >
              Preview Images
            </Button>
            <Button
              type="default"
              size="small"
              icon={<ClockCircleOutlined />}
              onClick={openScreenTimeSummary}
              style={{ marginLeft: 8 }}
            >
              Screen Time Summary
            </Button>
            <Button
              type="default"
              size="small"
              onClick={() => navigate(`/user-management/${userId}/new-location`)}
              style={{ marginLeft: 8 }}
            >
              Auto picked locations
            </Button>
            <Button
              type="default"
              size="small"
              icon={<CheckCircleOutlined />}
              onClick={() => navigate(`/user-management/${userId}/attendance?month=${moment(selectedDate).format("YYYY-MM")}`)}
              style={{ marginLeft: 8 }}
            >
              Attendance Summary
            </Button>
          </Card>
        </Col>
      </Row>
      <Row style={{ marginTop: 12 }}>
        <Col xs={24}>
          <Card
            title="Map"
            size="small"
            extra={
              <DatePicker
                allowClear
                value={selectedDate}
                onChange={(date) => setSelectedDate(date || moment())}
                style={{ marginBottom: 0 }}
              />
            }
          >
            <div style={{ height: MAP_HEIGHT, borderRadius: 8, overflow: "hidden", marginTop: 8 }}>
              <MapContainer
                center={mapCenter}
                zoom={hasLocations ? MAP_ZOOM : 5}
                style={{ height: "100%", width: "100%" }}
                scrollWheelZoom
              >
                <TileLayer
                  attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                  url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                />
                <MapFitBounds locations={routeLocations} />
                {routeLocations.map((loc, index) => {
                  const pos = [Number(loc.latitude), Number(loc.longitude)];
                  const timeOnly = loc.mailReceivedAt
                    ? new Date(loc.mailReceivedAt).toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" })
                    : "";
                  return (
                    <Marker key={index} position={pos}>
                      <Tooltip permanent direction="top" offset={[0, -24]}>
                        #{index + 1} {timeOnly ? `— ${timeOnly}` : ""}
                      </Tooltip>
                      <Popup>
                        <div style={{ minWidth: 180 }}>
                          <strong>#{index + 1}</strong> {timeOnly}
                          {loc.location && <div style={{ fontSize: 11, marginTop: 4 }}>{loc.location}</div>}
                          {loc.imageUrl && (
                            <div style={{ marginTop: 8 }}>
                              <img
                                src={loc.imageUrl}
                                alt=""
                                style={{
                                  maxWidth: "100%",
                                  width: 200,
                                  height: "auto",
                                  borderRadius: 8,
                                  display: "block",
                                }}
                              />
                            </div>
                          )}
                        </div>
                      </Popup>
                    </Marker>
                  );
                })}
              </MapContainer>
            </div>
          </Card>
          <Card
            title="Location history"
            size="small"
            style={{ marginTop: 16 }}
            extra={
              <Button
                type="primary"
                size="small"
                disabled={!imageTimeline.length}
                onClick={() => setPreviewVisible(true)}
              >
                Preview Images
              </Button>
            }
          >
            <Table
              dataSource={orderedLocations.map((loc, i) => ({ ...loc, key: i }))}
              columns={[
                { title: "#", dataIndex: "key", width: 48, render: (_, __, i) => i + 1 },
                {
                  title: "Date & Time",
                  dataIndex: "mailReceivedAt",
                  key: "mailReceivedAt",
                  render: (val) => val ? moment(val).format("DD MMM YYYY, hh:mm A") : "—",
                },
                { title: "Location", dataIndex: "location", key: "location", ellipsis: true, render: (v) => v || "—" },
                {
                  title: "Latitude",
                  dataIndex: "latitude",
                  key: "latitude",
                  width: 110,
                  render: (v) => (v != null ? Number(v).toFixed(5) : "—"),
                },
                {
                  title: "Longitude",
                  dataIndex: "longitude",
                  key: "longitude",
                  width: 110,
                  render: (v) => (v != null ? Number(v).toFixed(5) : "—"),
                },
                {
                  title: "Image",
                  dataIndex: "imageUrl",
                  key: "imageUrl",
                  width: 100,
                  render: (url) =>
                    url ? (
                      <Button type="link" size="small" onClick={() => setPreviewVisible(true)}>
                        Preview
                      </Button>
                    ) : (
                      "—"
                    ),
                },
              ]}
              pagination={{ pageSize: 10, showSizeChanger: true, pageSizeOptions: ["10", "20", "50"] }}
              size="small"
            />
          </Card>
          <Modal
            open={previewVisible}
            title="Image Preview with Time"
            onCancel={() => setPreviewVisible(false)}
            footer={null}
            width={860}
          >
            {imageTimeline.length ? (
              <Image.PreviewGroup>
                <div style={{ maxHeight: "70vh", overflowY: "auto", paddingRight: 4 }}>
                  {imageTimeline.map((item, index) => (
                    <div
                      key={item.key}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 12,
                        padding: "10px 0",
                        borderBottom: index === imageTimeline.length - 1 ? "none" : "1px solid #f0f0f0",
                      }}
                    >
                      <Image
                        src={item.imageUrl}
                        alt={`Location ${index + 1}`}
                        width={120}
                        height={90}
                        style={{ objectFit: "cover", borderRadius: 8, cursor: "pointer" }}
                      />
                      <div style={{ minWidth: 0 }}>
                        <div style={{ fontWeight: 600 }}>{item.timeText}</div>
                        <div style={{ color: "#8c8c8c", marginTop: 4, wordBreak: "break-word" }}>{item.locationText}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </Image.PreviewGroup>
            ) : (
              <Empty description="No images found for selected date" />
            )}
          </Modal>
          <Modal
            open={screenTimeVisible}
            title={`Screen Time Summary — ${moment(selectedDate).format("DD MMM YYYY")}`}
            onCancel={() => setScreenTimeVisible(false)}
            footer={null}
            width={560}
          >
            {screenTimeLoading ? (
              <div style={{ textAlign: "center", padding: 32 }}><Spin /></div>
            ) : screenTimeSummary ? (
              <div>
                <Row gutter={[12, 12]}>
                  <Col xs={24} sm={12}>
                    <Card size="small"><strong>Total Screen Time</strong><div style={{ fontSize: 22, marginTop: 6 }}>{screenTimeSummary.totalScreenTime || "0 min"}</div></Card>
                  </Col>
                  <Col xs={24} sm={12}>
                    <Card size="small"><strong>Shift Time</strong><div style={{ fontSize: 16, marginTop: 9 }}>{screenTimeSummary.shift ? `${screenTimeSummary.shift.start} - ${screenTimeSummary.shift.end}` : "Not configured"}</div></Card>
                  </Col>
                </Row>
                <div style={{ marginTop: 18, fontWeight: 600 }}>App-wise usage</div>
                {screenTimeSummary.appUsage?.length ? (
                  <Table
                    size="small"
                    pagination={false}
                    rowKey={(item) => item.appName}
                    dataSource={screenTimeSummary.appUsage}
                    columns={[
                      { title: "App", dataIndex: "appName", key: "appName" },
                      { title: "Time", dataIndex: "duration", key: "duration", width: 150 },
                    ]}
                  />
                ) : <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No app usage recorded in shift time" />}
              </div>
            ) : (
              <Empty description="Could not load screen time summary" />
            )}
          </Modal>
        </Col>
      </Row>
    </div>
  );
}

export default UserLocationMapPage;
